import React, {useEffect, useRef, useState, useCallback} from 'react';
import styled from '@emotion/styled';
import {keyframes} from '@emotion/react';

interface BottomModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title?: string;
    children: React.ReactNode;
    defaultButton?: boolean;
    bottomButton?: React.ReactNode;
    heightAuto?: boolean;
    radius?: number;
    zIndex?: number;
}

export default function BottomModal({
    isOpen,
    onClose,
    onConfirm,
    title,
    children,
    defaultButton = true,
    bottomButton,
    heightAuto = false,
    radius = 40,
    zIndex = 1000,
}: BottomModalProps) {
    const [mounted, setMounted] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchY, setTouchY] = useState<number>(0);
    const [justReleased, setJustReleased] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setTouchY(0);
        }
    }, [isOpen]);

    useEffect(() => {
        setMounted(true);
    }, []);

    const handleClose = useCallback(() => {
        if (isClosing) return;
        setIsClosing(true);
        setTimeout(() => {
            onClose();
            setIsClosing(false);
            setTouchY(0);
        }, 300);
    }, [isClosing, onClose]);

    const handleTouchStart = (e: React.TouchEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setTouchStart(e.touches[0].clientY);
        setTouchY(0);
        // console.log('Touch start:', e.touches[0].clientY);
        setJustReleased(true);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        e.stopPropagation();
        if (touchStart === null) return;

        const currentY = e.touches[0].clientY;
        const deltaY = currentY - touchStart;

        // console.log('Touch move:', {currentY, deltaY});

        if (deltaY > 0) {
            setTouchY(deltaY);
            e.preventDefault();
        }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        e.stopPropagation();
        if (touchStart === null || isClosing) return;
        // console.log('Touch end:', {touchY, touchStart});
        if (touchY > 50) {
            handleClose();
        } else {
            setTouchY(0);
        }
        setTouchStart(null);
        setJustReleased(false);
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setTouchStart(e.clientY);
        setTouchY(0);
        // console.log('Mouse down:', e.clientY);
    };

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('no-scroll');
        } else {
            document.body.classList.remove('no-scroll');
        }

        return () => {
            document.body.classList.remove('no-scroll');
        };
    }, [isOpen]);

    useEffect(() => {
        const handleTouchMove = (e: TouchEvent) => {
            const target = e.target as Node;
            // 다른 BottomModal 내부의 터치도 허용 (id="modal-wrapper" 사용)
            const isInAnyModal = (target as Element).closest?.('#modal-wrapper');
            if (!modalRef.current?.contains(target) && !isInAnyModal) {
                e.preventDefault();
            }
        };

        if (isOpen) {
            document.addEventListener('touchmove', handleTouchMove, {passive: false});
        }

        return () => {
            document.removeEventListener('touchmove', handleTouchMove);
        };
    }, [isOpen]);

    useEffect(() => {
        console.log(justReleased);
    }, [justReleased]);

    useEffect(() => {
        const handleGlobalMouseMove = (e: MouseEvent) => {
            if (touchStart !== null) {
                const currentY = e.clientY;
                const deltaY = currentY - touchStart;

                // console.log('Global mouse move:', {currentY, deltaY, touchStart});

                if (deltaY > 0) {
                    setTouchY(deltaY);
                    e.preventDefault();
                }
            }
        };

        const handleGlobalMouseUp = () => {
            if (touchStart !== null && !isClosing) {
                if (touchY > 50) {
                    handleClose();
                } else {
                    setTouchY(0);
                }

                setTouchStart(null);
                setJustReleased(true);
                setTimeout(() => setJustReleased(false), 100);
            }
        };

        if (touchStart !== null) {
            document.addEventListener('mousemove', handleGlobalMouseMove, {passive: false});
            document.addEventListener('mouseup', handleGlobalMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleGlobalMouseMove);
            document.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, [touchStart, touchY, isClosing, handleClose]);

    if (!mounted) return null;

    return (
        <BottomModalWrapper
            id='modal-wrapper'
            isOpening={isOpen}
            isClosing={isClosing}
            onClick={!justReleased ? handleClose : undefined}
            $zIndex={zIndex}
        >
            <ModalContent
                isOpening={isOpen}
                isClosing={isClosing}
                ref={modalRef}
                heightAuto={heightAuto}
                radius={radius}
                $zIndex={zIndex}
                style={{
                    transform: touchY > 0 ? `translateY(${touchY}px) !important` : undefined,
                    transition: touchY > 0 ? 'none !important' : undefined,
                    animation: touchY > 0 ? 'none !important' : undefined,
                    willChange: touchY > 0 ? 'transform' : undefined,
                    position: 'fixed',
                    bottom: touchY > 0 ? `${-touchY}px` : '0px',
                }}
            >
                <div
                    className='handle'
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onMouseDown={handleMouseDown}
                    onClick={e => e.stopPropagation()}
                >
                    <div className='handle-bar' />
                </div>
                {title && (
                    <div className='header' onClick={e => e.stopPropagation()}>
                        <h2>{title}</h2>
                    </div>
                )}
                <div className='content'>{children}</div>
            </ModalContent>
        </BottomModalWrapper>
    );
}

const slideIn = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
`;

const backgroundFadeIn = keyframes`
  from {
    opacity: 0;
    display: block;
  }
  to {
    opacity: 1;
  }
`;

const backgroundFadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
    display: none;
  }
`;

const BottomModalWrapper = styled.div<{isOpening: boolean; isClosing: boolean; $zIndex: number}>`
    width: 100%;
    max-width: var(--max-width);
    margin: 0 auto;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.7);
    opacity: ${props => (props.isOpening || props.isClosing ? 1 : 0)};
    animation: ${props => (props.isClosing ? backgroundFadeOut : props.isOpening ? backgroundFadeIn : 'none')} 0.3s
        ease-in-out forwards;
    display: ${props => (props.isOpening || props.isClosing ? 'flex' : 'none')};
    justify-content: center;
    align-items: flex-end;
    overflow: hidden;
    z-index: ${props => props.$zIndex};
    isolation: isolate;
`;

const ModalContent = styled.div<{
    isOpening: boolean;
    isClosing: boolean;
    heightAuto: boolean;
    radius: number;
    $zIndex: number;
}>`
    padding-top: 20px;
    padding-bottom: var(--safe-bottom-keyboard);
    transition: padding-bottom 200ms ease;
    width: 100%;
    ${props => !props.heightAuto && 'height: 100%;'}
    max-width: var(--max-width);
    margin: 0 auto;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: ${props => props.$zIndex + 1};

    background-color: white;
    border-radius: ${props => `${props.radius}px ${props.radius}px`} 0 0;
    max-height: 85dvh;
    display: flex;
    flex-direction: column;
    animation: ${props => (props.isClosing ? slideOut : props.isOpening ? slideIn : 'none')} 0.3s ease-in-out forwards;

    .handle {
        padding: 8px 0;
        width: 32px;
        height: 4px;
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        cursor: grab;
        touch-action: pan-y;
        user-select: none;
        -webkit-user-select: none;
        -webkit-touch-callout: none;

        .handle-bar {
            width: 32px;
            height: 4px;
            border-radius: 2px;
            background-color: #e6e6e6;
        }

        &:active {
            cursor: grabbing;
        }
    }

    & > .header {
        padding: 17px 24px 0px 24px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        h2 {
            color: #1a1a1a;
            font-size: 22px;
            font-weight: 700;
            line-height: 32px;
            letter-spacing: -0.44px;
        }
    }

    & > .content {
        padding: 15px;
        display: flex;
        flex-direction: column;
        /* flex: 1; */
        height: 100%;
        overflow-y: hidden;
        /* overflow-y: auto;
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none; */
    }
`;
